import { createSlice } from "@reduxjs/toolkit";
import * as PDFViewerActions from "./actions";
import { lensPath, pipe, propOr, set } from "ramda";
import * as AppActions from "../app/actions";

export const initialState = {
  page: 1,
  totalPages: 1,
  scale: 1,
  fileData: {
    file: null,
    url: null,
    pdfFileName: null,
    fileUploaded: true,
    fileUploadingFromUrl: false,
  },
  canvas: {
    canvasMarginLeft: 0,
    canvasHeight: 0,
    canvasWidth: 0,
  },
  currentPageOriginal: {
    currentPageOriginalHeight: 1,
    currentPageOriginalWidth: 1,
    currentPageOriginalRotationAngle: 0,
  },
  convertingToPdf: false,
  isCustomFile: false,
};

const pdfViewerSlice = createSlice({
  name: "PDFViewer",
  initialState,
  reducers: {},
  extraReducers: {
    [AppActions.resetAppState.type]: (state, action) => {
      return initialState;
    },
    [PDFViewerActions.resetPdfFileData.type]: (state, action) => {
      return pipe(
        set(lensPath(["fileData", "file"]), null),
        set(lensPath(["fileData", "url"]), null),
        set(lensPath(["fileData", "pdfFileName"]), null),
        set(lensPath(["fileData", "fileUploaded"]), true),
        set(lensPath(["fileData", "fileUploadingFromUrl"]), false),
      )(state);
    },

    [PDFViewerActions.setPdfFileName.type]: (state, action) => {
      return set(
        lensPath(["fileData", "pdfFileName"]),
        propOr(null, "pdfFileName", action.payload)
      )(state);
    },

    [PDFViewerActions.resetPdfFileAndUrl.type]: (state) => {
      return pipe(
        set(lensPath(["fileData", "file"]), null),
        set(lensPath(["fileData", "url"]), null)
      )(state);
    },

    [PDFViewerActions.setIsCustomFile.type]: (state, action) => {
      return {
        ...state,
        isCustomFile: action.payload.isCustomFile,
      };
    },

    [PDFViewerActions.requestConvertToPdf.type]: (state) => {
      return {
        ...state,
        convertingToPdf: true,
      };
    },

    [PDFViewerActions.receiveConvertToPdf.type]: (state) => {
      return {
        ...state,
        convertingToPdf: false,
      };
    },

    [PDFViewerActions.setPDFFile.type]: (state, action) => {
      return pipe(
        set(
          lensPath(["fileData", "file"]),
          propOr(null, "file", action.payload)
        ),
        set(lensPath(["fileData", "url"]), null)
      )(state);
    },

    [PDFViewerActions.setPDFUrl.type]: (state, action) => {
      return pipe(
        set(lensPath(["fileData", "url"]), propOr(null, "url", action.payload))
      )(state);
    },

    [PDFViewerActions.setPdfPage.type]: (state, action) => {
      return {
        ...state,
        page: action.payload.page,
      };
    },

    [PDFViewerActions.setPdfTotalPages.type]: (state, action) => {
      return {
        ...state,
        totalPages: action.payload.page,
      };
    },

    [PDFViewerActions.setPdfScale.type]: (state, action) => {
      return {
        ...state,
        scale: action.payload.scale,
      };
    },

    [PDFViewerActions.setCanvasMarginLeft.type]: (state, action) => {
      return set(
        lensPath(["canvas", "canvasMarginLeft"]),
        propOr(null, "canvasMarginLeft", action.payload)
      )(state);
    },

    [PDFViewerActions.setCanvasDimensions.type]: (state, action) => {
      return pipe(
        set(
          lensPath(["canvas", "canvasHeight"]),
          propOr(null, "canvasHeight", action.payload)
        ),
        set(
          lensPath(["canvas", "canvasWidth"]),
          propOr(null, "canvasWidth", action.payload)
        )
      )(state);
    },

    [PDFViewerActions.setCanvasCurrentPageOriginalDimensions.type]: (
      state,
      action
    ) => {
      return pipe(
        set(
          lensPath(["currentPageOriginal", "currentPageOriginalHeight"]),
          propOr(null, "currentPageOriginalHeight", action.payload)
        ),
        set(
          lensPath(["currentPageOriginal", "currentPageOriginalWidth"]),
          propOr(null, "currentPageOriginalWidth", action.payload)
        ),
        set(
          lensPath(["currentPageOriginal", "currentPageOriginalRotationAngle"]),
          propOr(null, "currentPageOriginalRotationAngle", action.payload)
        )
      )(state);
    },

    [PDFViewerActions.markFailToFileUploaded.type]: (state, action) => {
      return set(lensPath(["fileData", "fileUploaded"]), false)(state);
    },

    [PDFViewerActions.markSuccessToFileUploaded.type]: (state, action) => {
      return set(lensPath(["fileData", "fileUploaded"]), true)(state);
    },
  },
});

export const { actions, reducer, name: sliceKey } = pdfViewerSlice;
